import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { eventService, invitationService } from '../services/db';
import { useAuth } from '../contexts/AuthContext';
import InvitationList from '../components/InvitationList';
import ConfirmationDialog from '../components/ConfirmationDialog';
import './EventDetailPage.css';
import eventIcon from '../assets/event.png';

function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [event, setEvent] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [invitationToDelete, setInvitationToDelete] = useState(null);
  
  useEffect(() => {
    console.log("🔍 EventDetailPage mounted with eventId:", eventId);
    return () => console.log("🔍 EventDetailPage unmounted");
  }, [eventId]);
  
  useEffect(() => {
    // Load event details
    const loadEvent = async () => {
      try {
        console.log("Loading event with ID:", eventId, "Type:", typeof eventId);
        
        if (!currentUser) {
          navigate('/login');
          return;
        }
        
        // Try to get the event with both string and number ID
        let foundEvent = null;
        try {
          // First try with original ID
          foundEvent = await eventService.getById(eventId);
        } catch (err) {
          console.error("Error with original ID:", err);
        }
        
        if (!foundEvent) {
          try {
            // Then try with parsed ID
            foundEvent = await eventService.getById(parseInt(eventId, 10));
          } catch (err) {
            console.error("Error with parsed ID:", err);
          }
        }
        
        console.log("Found event:", foundEvent);
        
        if (foundEvent) {
          setEvent(foundEvent);
          console.log("🔍 Event set in state:", foundEvent.id, foundEvent.name);
          
          // Now get the invitations with better error handling
          try {
            console.log("🚨 DEBUG: About to fetch invitations for event:", foundEvent.id);
            
            // Explicitly convert to string
            const stringEventId = String(foundEvent.id);
            console.log("🚨 DEBUG: Using stringified event ID:", stringEventId);
            
            const eventInvitations = await eventService.getInvitations(stringEventId);
            
            // Very important - log exactly what we got back
            console.log("🚨 DEBUG: Invitations returned:", eventInvitations);
            console.log("🚨 DEBUG: Is array?", Array.isArray(eventInvitations));
            console.log("🚨 DEBUG: Length:", eventInvitations?.length);
            
            // Ensure we're updating state correctly
            if (Array.isArray(eventInvitations) && eventInvitations.length > 0) {
              console.log("🚨 DEBUG: Setting invitations state with", eventInvitations.length, "items");
              setInvitations([...eventInvitations]); // Create new array reference
            } else {
              console.log("🚨 DEBUG: No invitations or invalid data, setting empty array");
              setInvitations([]);
            }
          } catch (err) {
            console.error("Error loading invitations:", err);
            setInvitations([]);
          }
          
          // And get the guest list
          try {
            const eventGuests = await eventService.getGuests(foundEvent.id);
            console.log("Found guests:", eventGuests);
            setGuests(eventGuests || []);
          } catch (err) {
            console.error("Error loading guests:", err);
            setGuests([]);
          }
        } else {
          console.error("Event not found!");
          setError("Event not found.");
          // Event not found, redirect back to events page
          // navigate('/events');
          // Commented out to prevent immediate redirect so you can debug
        }
      } catch (error) {
        console.error("Error in loadEvent:", error);
        setError("Failed to load event data.");
      } finally {
        setLoading(false);
      }
    };
    
    loadEvent();
  }, [eventId, navigate, currentUser, refreshCounter]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  const handleDeleteInvitation = async (invitationId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Open confirmation dialog instead of using window.confirm
    setInvitationToDelete(invitationId);
    setIsDeleteDialogOpen(true);
  };
  
  const confirmDeleteInvitation = async () => {
    try {
      console.log("Deleting invitation:", invitationToDelete);
      await invitationService.delete(invitationToDelete);
      
      // Update the invitations list by removing the deleted one
      setInvitations(prev => prev.filter(inv => inv.id !== invitationToDelete));
      
      // Close the dialog
      setIsDeleteDialogOpen(false);
      setInvitationToDelete(null);
    } catch (error) {
      console.error("Failed to delete invitation:", error);
      alert("Failed to delete invitation. Please try again.");
      
      // Close the dialog
      setIsDeleteDialogOpen(false);
      setInvitationToDelete(null);
    }
  };
  
  const cancelDeleteInvitation = () => {
    // Close the dialog without deleting
    setIsDeleteDialogOpen(false);
    setInvitationToDelete(null);
  };
  
  // Add this function to create URL-friendly slugs and handle link clicks
  const createSlug = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleLinkClick = (invitation, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!invitation.published) {
      // If not published, don't do anything
      return;
    }
    
    // Add user ID to the URL format
    const eventSlug = createSlug(event?.name || 'event');
    const invitationSlug = createSlug(invitation.name);
    
    // Get the user ID prefix - use the first 5 chars of the userId
    const userIdentifier = event?.userId?.substring(0, 5) || currentUser?.id?.substring(0, 5) || 'user';
    
    // Get the uniqueId for the invitation - first 6 chars of invitation ID
    const invUniqueId = invitation?.id?.substring(0, 6) || '';
    
    // Build the correct URL format with user ID and invitation unique ID
    const baseUrl = window.location.origin;
    const invitationUrl = `${baseUrl}/${userIdentifier}/${eventSlug}/${invitationSlug}-${invUniqueId}`;
    
    // Copy to clipboard
    navigator.clipboard.writeText(invitationUrl)
      .then(() => {
        // Show temporary success message
        const successMessage = document.createElement('div');
        successMessage.className = 'url-copy-success';
        successMessage.textContent = 'URL copied to clipboard!';
        document.body.appendChild(successMessage);
        
        setTimeout(() => {
          document.body.removeChild(successMessage);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
        alert('Failed to copy URL. Please try again.');
      });
  };
  
  // Add a function to force refresh the invitation list
  const refreshInvitations = () => {
    console.log("🔄 Forcing refresh of invitations list");
    setRefreshCounter(prev => prev + 1);
  };
  
  if (loading) {
    return <div className="loading">Loading event details...</div>;
  }
  
  if (!event) {
    return <div className="error">{error}</div>;
  }
  
  return (
    <div className="event-detail-page">
      <Header />
      
      <div className="event-hero">
        <div className="back-button-container">
          <Link to="/events" className="back-button">
            ← Back to Events
          </Link>
        </div>
        
        <div className="event-header">
          <h1 className="event-name">{event.name}</h1>
        </div>
        
        {/* Add event icon as decorative element */}
        <img src={eventIcon} alt="" className="decorative-event-icon" />
        
        <div className="event-meta-info">
          <div className="meta-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19Z" fill="currentColor"/>
            </svg>
            {event.date ? formatDate(event.date) : 'No date set'}
          </div>
          
          <div className="meta-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="currentColor"/>
            </svg>
            {event.location?.venue || event.location?.city || 'No location set'}
          </div>
          
          <Link to={`/events/edit/${eventId}`} className="edit-event-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 17.25V21H6.75L17.81 9.94L14.06 6.19L3 17.25ZM20.71 7.04C21.1 6.65 21.1 6.02 20.71 5.63L18.37 3.29C17.98 2.9 17.35 2.9 16.96 3.29L15.13 5.12L18.88 8.87L20.71 7.04Z" fill="currentColor"/>
            </svg>
            Edit
          </Link>
        </div>
      </div>
      
      <div className="event-tools-container">
        <div className="event-tools">
          <div className="tool-card">
            <div className="tool-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 18H4V8L12 13L20 8V18ZM12 11L4 6H20L12 11Z" fill="#333"/>
              </svg>
            </div>
            <div className="tool-content">
              <h2>Invitations</h2>
              <p>Create and manage your event invitations</p>
              <div className="tool-actions">
                <Link 
                  to={`/events/${eventId}/templates`}
                  className="tool-button"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="currentColor"/>
                  </svg>
                  New Invitation
                </Link>
                <button 
                  className="refresh-button"
                  onClick={refreshInvitations}
                  title="Refresh invitation list"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="currentColor"/>
                  </svg>
                  Refresh
                </button>
              </div>
            </div>
            
            {invitations.length > 0 ? (
              <div className="tool-items">
                {console.log("🔍 Rendering", invitations.length, "invitations")}
                {invitations.map(invitation => (
                  <div key={invitation.id} className="item-row">
                    {console.log("🔍 Rendering invitation:", invitation.id, invitation.name)}
                    <div className="item-details">
                      <span className="item-name">{invitation.name}</span>
                      <span className="item-status">{invitation.published ? "Published" : "Draft"}</span>
                    </div>
                    <div className="item-actions">
                      <Link to={`/events/${eventId}/invitation/${invitation.id}/edit`} className="item-action">
                        Edit
                      </Link>
                      <button 
                        className={`item-action link-icon-btn ${invitation.published ? 'published' : 'unpublished'}`}
                        onClick={(e) => handleLinkClick(invitation, e)}
                        title={invitation.published ? "Copy invitation URL" : "Not published yet"}
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="18" 
                          height="18" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                        </svg>
                      </button>
                      <button 
                        className="item-action delete-btn" 
                        onClick={(e) => handleDeleteInvitation(invitation.id, e)}
                        title="Delete Invitation"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-items-message">
                {console.log("🔍 No invitations to render")}
                No invitations created yet
              </div>
            )}
          </div>
          
          <div className="tool-card">
            <div className="tool-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z" fill="#333"/>
              </svg>
            </div>
            <div className="tool-content">
              <h2>Guest List</h2>
              <p>View and manage your event RSVPs</p>
              <Link 
                to={`/rsvp`} 
                className="tool-button"
              >
                View RSVPs
              </Link>
            </div>
            
            <div className="tool-stats">
              <div className="stat-item">
                <span className="stat-value">
                  {guests.reduce((total, guest) => {
                    // Add the guest count (person + any additional guests)
                    return total + (parseInt(guest.guestCount) || 1);
                  }, 0)}
                </span>
                <span className="stat-label">Total Guests</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">
                  {guests.reduce((total, guest) => {
                    // For attending count, only count if status is attending and include their guests
                    return total + (guest.rsvpStatus === 'attending' ? (parseInt(guest.guestCount) || 1) : 0);
                  }, 0)}
                </span>
                <span className="stat-label">Attending</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{guests.length}</span>
                <span className="stat-label">RSVP Count</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Invitation"
        message="This action cannot be undone. The invitation will be permanently removed."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteInvitation}
        onCancel={cancelDeleteInvitation}
        type="delete"
      />
    </div>
  );
}

export default EventDetailPage; 